import { extractPlasmicQueryData, PlasmicComponent, PlasmicRootProvider } from '@plasmicapp/loader-nextjs';
import { GetStaticPaths, GetStaticProps } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';

import { SeoOverrides } from '~/components/Seo/SeoOverrides';
import { fetchAutoLoadedPages, getSeoOverrides, PLASMIC, PlasmicProps, revalidateInterval } from '~/helpers/plasmic';
import Config from '~/lib/config';
import { getTranslations } from '~/lib/next-i18next';

import FourOhFour from '../404';

export const getStaticPaths: GetStaticPaths = async () => {
  const autoLoadedPages = await fetchAutoLoadedPages();
  return {
    paths: autoLoadedPages.map((page) => ({
      params: { catchall: page.path.substring(1).split('/') },
    })),
    fallback: 'blocking',
  };
};

// Pre-fetch the data Plasmic needs for each page.
export const getStaticProps: GetStaticProps = async ({ params, locale }) => {
  const { catchall } = params ?? {};

  // Convert the catchall param into a path string.
  let plasmicPath = catchall;
  if (typeof plasmicPath !== 'string') {
    plasmicPath = Array.isArray(catchall) ? `/${catchall.join('/')}` : '/';
  }
  const plasmicData = await PLASMIC.maybeFetchComponentData(plasmicPath);
  if (!plasmicData) {
    // This path does not exist in Plasmic (or is unpublished).
    return {
      notFound: true,
      revalidate: revalidateInterval,
    };
  }
  if (locale !== 'en') {
    return {
      redirect: {
        destination: plasmicPath,
        statusCode: 301,
      },
    };
  }

  // This path exists in Plasmic.
  const pageMeta = plasmicData.entryCompMetas[0];

  // Cache the necessary data fetched for the page.
  const queryCache = await extractPlasmicQueryData(
    <PlasmicRootProvider loader={PLASMIC} prefetchedData={plasmicData} pageParams={pageMeta.params}>
      <PlasmicComponent component={pageMeta.displayName} />
    </PlasmicRootProvider>,
  );

  return {
    // Pass the data as props.
    props: {
      plasmicData,
      plasmicPath,
      queryCache,
      // If PlasmicPage renders FourOhFour, we'll need translations.
      ...(await getTranslations({
        locale,
        namespaces: 'fourOhFour',
      })),
    },
    // Use incremental static regeneration.
    revalidate: revalidateInterval,
  };
};

const PlasmicPage = ({ plasmicData, plasmicPath, queryCache }: PlasmicProps) => {
  const router = useRouter();
  if (!plasmicData || plasmicData.entryCompMetas.length === 0) {
    return <FourOhFour />;
  }
  const pageMeta = plasmicData.entryCompMetas[0];
  const overrides = getSeoOverrides(plasmicData);
  return (
    // Pass in the data fetched in getStaticProps as prefetchedData.
    // pageMeta.displayName contains the name of the component you fetched.
    <PlasmicRootProvider
      loader={PLASMIC}
      prefetchedData={plasmicData}
      prefetchedQueryData={queryCache}
      pageParams={pageMeta.params}
      pageQuery={router.query}
    >
      <Head>
        <link rel="canonical" href={Config.SITE + plasmicPath} />
      </Head>
      <SeoOverrides {...overrides} />
      <PlasmicComponent
        component={pageMeta.displayName}
        componentProps={{
          // Use our default font and line height, not Plasmic's (Inter and 1.5).
          style: {
            fontFamily: 'Karla, sans-serif',
            lineHeight: '140%',
          },
        }}
      />
    </PlasmicRootProvider>
  );
};

export default PlasmicPage;
